import React, { useState, useMemo, useRef } from "react"
import "./DynamicDashboard.css"

const DynamicDashboard = ({
  data = [],
  filterFields = [],
  hierarchyFields = [],
  valueField = "total",
  yearField = "year",
  years = [],
  title = "Dashboard",
  onDrill, // nouvelle prop
}) => {
  const [filters, setFilters] = useState(() => {
    // Filtre par défaut sur "Wonder Grip Customers"
    const defaultFilters = {}
    if (filterFields.includes('buyerN1')) {
      defaultFilters.buyerN1 = { 'Wonder Grip Customers': true }
    }
    return defaultFilters
  })
  const [expanded, setExpanded] = useState({})
  const [sortConfig, setSortConfig] = useState({ key: 'total', direction: 'desc' })
    const [columnWidths, setColumnWidths] = useState(() => {
    // Largeurs fixes - plus de calcul automatique !
    const widths = { name: 100 }
    years.forEach(year => {
      widths[year] = 50
    })
    widths.total = 50
    return widths
  })
  
  const resizingRef = useRef(null)

  // État de recherche par filtre
  const [searches, setSearches] = useState({})
  const [searchOpen, setSearchOpen] = useState({})

  // Gérer le resize des colonnes
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault()
    resizingRef.current = { columnKey, startX: e.clientX, startWidth: columnWidths[columnKey] }

    const handleMouseMove = (event) => {
      if (!resizingRef.current) return
      const diff = event.clientX - resizingRef.current.startX
      const newWidth = Math.max(80, resizingRef.current.startWidth + diff)

      setColumnWidths(prev => ({
        ...prev,
        [resizingRef.current.columnKey]: newWidth,
      }))
    }

    const handleMouseUp = () => {
      resizingRef.current = null
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  // Générer les options de filtres dynamiquement
  const filterOptions = useMemo(() => {
    const options = {}
    filterFields.forEach(field => {
      options[field] = [...new Set(data.map(item => item[field]).filter(Boolean))]
    })
    return options
  }, [data, filterFields])

  // Gérer le changement de filtre
  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: {
        ...prev[filterType],
        [value]: !prev[filterType]?.[value],
      },
    }))
  }

  // Filtrer les données
  const filteredData = useMemo(() => {
    return data.filter(item => {
      return filterFields.every(field => {
        const activeFilters = Object.entries(filters[field] || {})
          .filter(([, active]) => active)
          .map(([value]) => value)
        
        return activeFilters.length === 0 || activeFilters.includes(item[field])
      })
    })
  }, [data, filters, filterFields])

  // Construire la hiérarchie
  const hierarchicalData = useMemo(() => {
    if (hierarchyFields.length === 0) return filteredData

    const buildHierarchy = (items, fieldIndex = 0) => {
      if (fieldIndex >= hierarchyFields.length) {
        // Niveau feuille : grouper par année et sommer
        const yearTotals = {}
        years.forEach(year => {
          yearTotals[year] = items
            .filter(item => item[yearField] === year)
            .reduce((sum, item) => sum + (item[valueField] || 0), 0)
        })
        return {
          yearTotals,
          total: Object.values(yearTotals).reduce((sum, val) => sum + val, 0),
          items,
          isLeaf: true,
        }
      }

      const currentField = hierarchyFields[fieldIndex]
      const grouped = {}

      items.forEach(item => {
        const key = item[currentField]
        if (!grouped[key]) grouped[key] = []
        grouped[key].push(item)
      })

      return Object.entries(grouped).map(([key, groupItems]) => ({
        name: key,
        field: currentField,
        level: fieldIndex,
        isLeaf: fieldIndex === hierarchyFields.length - 1,
        children: buildHierarchy(groupItems, fieldIndex + 1),
        yearTotals: years.reduce((acc, year) => {
          acc[year] = groupItems
            .filter(item => item[yearField] === year)
            .reduce((sum, item) => sum + (item[valueField] || 0), 0)
          return acc
        }, {}),
        total: groupItems.reduce((sum, item) => sum + (item[valueField] || 0), 0),
      }))
    }

    return buildHierarchy(filteredData)
  }, [filteredData, hierarchyFields, years, yearField, valueField])

  // Calculer les totaux généraux
  const grandTotals = useMemo(() => {
    const totals = {}
    years.forEach(year => {
      totals[year] = filteredData
        .filter(item => item[yearField] === year)
        .reduce((sum, item) => sum + (item[valueField] || 0), 0)
    })
    totals.total = Object.values(totals).reduce((sum, val) => sum + val, 0)
    return totals
  }, [filteredData, years, yearField, valueField])

  // Gérer l'expansion/collapse
  const toggleExpanded = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
  }

  // Gérer le tri
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' ? 'asc' : 'desc',
    }))
  }

  // Trier les données hiérarchiques
  const sortedHierarchicalData = useMemo(() => {
    if (!sortConfig.key || !Array.isArray(hierarchicalData)) return hierarchicalData

    const sortItems = (items) => {
      const sorted = [...items].sort((a, b) => {
        let aVal, bVal
        
        if (sortConfig.key === 'name') {
          aVal = a.name
          bVal = b.name
        } else if (sortConfig.key === 'total') {
          aVal = a.total
          bVal = b.total
        } else {
          aVal = a.yearTotals[sortConfig.key] || 0
          bVal = b.yearTotals[sortConfig.key] || 0
        }

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortConfig.direction === 'desc' ? bVal - aVal : aVal - bVal
        }
        
        const aStr = String(aVal || '').toLowerCase()
        const bStr = String(bVal || '').toLowerCase()
        return sortConfig.direction === 'desc' ? bStr.localeCompare(aStr) : aStr.localeCompare(bStr)
      })

      return sorted.map(item => ({
        ...item,
        children: Array.isArray(item.children) ? sortItems(item.children) : item.children,
      }))
    }

    return sortItems(hierarchicalData)
  }, [hierarchicalData, sortConfig])

  // Icône d'expansion moderne
  const renderExpandIcon = (isExpanded, hasChildren) => {
    if (!hasChildren) return <span className="expand-spacer"></span>
    
    return (
      <span className={`expand-icon ${isExpanded ? 'expanded' : 'collapsed'}`}>
        {isExpanded ? '−' : '+'}
      </span>
    )
  }

  // Icône de tri
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <span className="sort-icon">↕</span>
    return <span className="sort-icon active">{sortConfig.direction === 'desc' ? '↓' : '↑'}</span>
  }

  // Rendu des filtres
  const renderFilters = () => (
    <div className="dashboard-filters dashboard-fadein">
      <h3 className="dashboard-title" style={{ fontSize: 16, margin: '0 0 16px 0' }}>Filters</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
        {filterFields.map(field => {
          // Filtrage dynamique selon la recherche
          const search = searches[field] || ""
          const options = filterOptions[field]?.filter(option =>
            option.toLowerCase().includes(search.toLowerCase())
          )
          return (
            <div key={field} className="dashboard-filter-group" style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h4 style={{ margin: 0 }}>{field}</h4>
                <span
                  className="dashboard-filter-search-icon"
                  tabIndex={0}
                  role="button"
                  aria-label={`Rechercher dans ${field}`}
                  onClick={() => setSearchOpen(prev => ({ ...prev, [field]: !prev[field] }))}
                  style={{ cursor: 'pointer', marginLeft: 6, fontSize: 14, color: '#4790C9', opacity: 0.7 }}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><circle cx="9" cy="9" r="7" stroke="#4790C9" strokeWidth="2"/><path d="M15 15L19 19" stroke="#4790C9" strokeWidth="2" strokeLinecap="round"/></svg>
                </span>
              </div>
              {/* Champ de recherche inline */}
              {searchOpen[field] && (
                <div className="dashboard-filter-search-inline" style={{ margin: '6px 0 6px 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    autoFocus
                    type="text"
                    className="dashboard-filter-search-input"
                    placeholder={`Rechercher...`}
                    value={search}
                    onChange={e => setSearches(prev => ({ ...prev, [field]: e.target.value }))}
                    style={{ width: '100%', padding: '4px 8px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: 13 }}
                  />
                  <span
                    className="dashboard-filter-search-close"
                    style={{ cursor: 'pointer', color: '#888', fontSize: 13 }}
                    onClick={() => { setSearchOpen(prev => ({ ...prev, [field]: false })); setSearches(prev => ({ ...prev, [field]: '' })); }}
                  >✕</span>
                </div>
              )}
              <div className="dashboard-scroll">
                {options.map(option => (
                  <label key={option} className="dashboard-checkbox-label">
                    <input
                      type="checkbox"
                      className="dashboard-checkbox"
                      checked={filters[field]?.[option] || false}
                      onChange={() => handleFilterChange(field, option)}
                    />
                    {option}
                  </label>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )

  // Rendu d'une ligne de la hiérarchie
  const renderHierarchyRow = (item, path = "") => {
    const key = `${path}-${item.name}`
    const isExpanded = expanded[key]
    const hasChildren = Array.isArray(item.children) && item.children.length > 0
    const indent = item.level * 20 // Espacement réduit par niveau

    return (
      <React.Fragment key={key}>
        <tr className={`dashboard-table-row ${!item.isLeaf ? 'parent-row' : 'leaf-row'}`}>
          <td 
            className="dashboard-table-cell dashboard-table-cell-left" 
            style={{ 
              paddingLeft: 8 + indent,
              width: `${columnWidths.name}px`,
              minWidth: `${columnWidths.name}px`,
              maxWidth: `${columnWidths.name}px`
            }}
          >
            <span 
              onClick={() => hasChildren && toggleExpanded(key)}
              style={{ cursor: hasChildren ? 'pointer' : 'default', marginRight: 8 }}
            >
              {renderExpandIcon(isExpanded, hasChildren)}
            </span>
            <span className={!item.isLeaf ? 'parent-text' : ''}>{item.name}</span>
          </td>
          {years.map(year => (
            <td 
              key={year} 
              className={`dashboard-table-cell dashboard-table-cell-right ${!item.isLeaf ? 'parent-text' : ''}`}
              style={{ 
                width: `${columnWidths[year]}px`,
                minWidth: `${columnWidths[year]}px`,
                maxWidth: `${columnWidths[year]}px`
              }}
            >
              {(item.yearTotals[year] || 0).toLocaleString()}
            </td>
          ))}
          <td 
            className={`dashboard-table-cell dashboard-table-cell-right ${!item.isLeaf ? 'parent-text' : ''}`}
            style={{ 
              width: `${columnWidths.total}px`,
              minWidth: `${columnWidths.total}px`,
              maxWidth: `${columnWidths.total}px`
            }}
          >
            {item.total.toLocaleString()}
          </td>
        </tr>
        {hasChildren && isExpanded && item.children.map(child => 
          renderHierarchyRow(child, key)
        )}
      </React.Fragment>
    )
  }

  const allColumns = ['name', ...years, 'total']

  return (
    <div className="dashboard-container">
      {renderFilters()}
      <h2 className="dashboard-title">{title}</h2>
      <div className="dashboard-table-container">
        <table className="dashboard-table" style={{ tableLayout: 'fixed' }}>
          <thead className="dashboard-table-header-fixed">
            <tr className="dashboard-table-header">
              <th 
                className="dashboard-table-cell dashboard-table-cell-left sortable-header"
                onClick={() => handleSort('name')}
                style={{ 
                  width: `${columnWidths.name}px`,
                  minWidth: `${columnWidths.name}px`,
                  maxWidth: `${columnWidths.name}px`,
                  position: 'relative'
                }}
              >
                {hierarchyFields.join(' > ')} {getSortIcon('name')}
                <div
                  className="column-resizer"
                  onMouseDown={(e) => handleMouseDown(e, 'name')}
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    width: '4px',
                    cursor: 'col-resize',
                    backgroundColor: 'transparent',
                    zIndex: 10
                  }}
                  onMouseEnter={(e) => { e.target.style.backgroundColor = '#d1d5db' }}
                  onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent' }}
                />
              </th>
              {years.map((year, index) => (
                <th 
                  key={year} 
                  className="dashboard-table-cell dashboard-table-cell-right sortable-header"
                  onClick={() => handleSort(year)}
                  style={{ 
                    width: `${columnWidths[year]}px`,
                    minWidth: `${columnWidths[year]}px`,
                    maxWidth: `${columnWidths[year]}px`,
                    position: 'relative'
                  }}
                >
                  {year} {getSortIcon(year)}
                  {index < years.length && (
                    <div
                      className="column-resizer"
                      onMouseDown={(e) => handleMouseDown(e, year)}
                      style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        cursor: 'col-resize',
                        backgroundColor: 'transparent',
                        zIndex: 10
                      }}
                      onMouseEnter={(e) => { e.target.style.backgroundColor = '#d1d5db' }}
                      onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent' }}
                    />
                  )}
                </th>
              ))}
              <th 
                className="dashboard-table-cell dashboard-table-cell-right sortable-header"
                onClick={() => handleSort('total')}
                style={{ 
                  width: `${columnWidths.total}px`,
                  minWidth: `${columnWidths.total}px`,
                  maxWidth: `${columnWidths.total}px`
                }}
              >
                Total {getSortIcon('total')}
              </th>
            </tr>
          </thead>
          <tbody className="dashboard-table-body">
            {Array.isArray(sortedHierarchicalData) && sortedHierarchicalData.length > 0 ? 
              sortedHierarchicalData.map(item => renderHierarchyRow(item)) :
              <tr><td colSpan={years.length + 2} style={{ textAlign: 'center', padding: '40px 20px', color: '#6b7280', fontStyle: 'italic' }}>No data to display</td></tr>
            }
            {/* Ligne Total intégrée dans le tbody */}
            <tr className="dashboard-table-total-row">
              <td 
                className="dashboard-table-cell dashboard-table-cell-left total-cell"
                style={{ 
                  paddingLeft: 8,
                  width: `${columnWidths.name}px`,
                  minWidth: `${columnWidths.name}px`,
                  maxWidth: `${columnWidths.name}px`
                }}
              >
                <span className="expand-spacer"></span>
                <strong>Total</strong>
              </td>
              {years.map(year => (
                <td 
                  key={year} 
                  className="dashboard-table-cell dashboard-table-cell-right total-cell"
                  style={{ 
                    width: `${columnWidths[year]}px`,
                    minWidth: `${columnWidths[year]}px`,
                    maxWidth: `${columnWidths[year]}px`
                  }}
                >
                  <strong>{(grandTotals[year] || 0).toLocaleString()}</strong>
                </td>
              ))}
              <td 
                className="dashboard-table-cell dashboard-table-cell-right total-cell"
                style={{ 
                  width: `${columnWidths.total}px`,
                  minWidth: `${columnWidths.total}px`,
                  maxWidth: `${columnWidths.total}px`
                }}
              >
                <strong>{grandTotals.total.toLocaleString()}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default DynamicDashboard 